import { BaseTool } from '../../core/BaseTool';
import { UploadManager } from '../image-converter/uploadManager';
import { FormatSelector } from '../../ui/formatSelector';
import { Modal } from '../../ui/modal';
import { AppIcon } from '../image-generator/appIcon';
import { TaglineManager } from '../image-generator/taglineManager';
import { CookieConsentController } from '../../services/cookieConsentController';
import { downloadBlob } from '../../utils/downloadHelper';

import { CanvasManager } from './core/CanvasManager';
import { ImageEditorEngine } from './core/ImageEditorEngine';
import { CropOverlay } from './ui/CropOverlay';
import { $ } from './ui/dom';

export class AdvancedImageEditorTool extends BaseTool {
  private uploadManager: UploadManager;
  private formatSelector: FormatSelector;
  private modal: Modal;
  private appIcon: AppIcon;

  private engine!: ImageEditorEngine;
  private cm!: CanvasManager;
  private cropOverlay!: CropOverlay;

  private selectedFormat = 'png';
  private resizeAspectLocked = true;
  private resizeAspectRatio = 1;

  constructor() {
    super();
    this.uploadManager = new UploadManager();
    this.formatSelector = new FormatSelector();
    this.modal = new Modal();
    this.appIcon = new AppIcon(this.modal);
  }

  async init(): Promise<void> {
    console.log('[AdvancedImageEditorTool] Starting initialization...');

    // 1. App icon & tagline
    this.appIcon.setRandomIcon();
    new TaglineManager();

    // 2. Cookie consent
    new CookieConsentController();

    // 3. Register modal globally
    (window as any).appModal = this.modal;

    // 4. Format selector
    this.formatSelector.init(
      'advFormatSelectorContainer',
      ['png', 'jpeg', 'webp'],
      'png',
      (format: string) => { this.selectedFormat = format; }
    );

    // 5. Upload manager
    this.uploadManager.init(this.handleFileUpload.bind(this));

    // 6. Set up dual canvas + engine
    const canvas = $('#advEditorCanvas') as HTMLCanvasElement;
    const overlay = $('#advOverlayCanvas') as HTMLCanvasElement;
    const container = $('#advCanvasContainer');

    this.cm = new CanvasManager(canvas, overlay, container);
    this.engine = new ImageEditorEngine(this.cm);

    // 7. Set up crop overlay
    const cropApplyBtn = $('#cropApplyBtn');
    const cropCancelBtn = $('#cropCancelBtn');
    this.cropOverlay = new CropOverlay(
      this.cm,
      cropApplyBtn,
      cropCancelBtn,
      (rect) => {
        this.engine.applyCrop(rect);
        this.engine.exitCropMode();
      },
      () => {
        this.engine.exitCropMode();
      },
    );

    // 8. Wire engine events
    this.wireEngineEvents();

    // 9. Wire UI controls
    this.wireSliders();
    this.wireCheckboxes();
    this.wireToolbar();
    this.wireTransformButtons();
    this.wireResizeControls();
    this.wireKeyboardShortcuts();
    this.wireDownloadButton();
    this.wireClearButton();

    this.setInitialized(true);
    console.log('[AdvancedImageEditorTool] Initialized successfully');
  }

  private handleFileUpload(file: File): void {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      this.engine.loadImage(img);
    };
    img.src = url;
  }

  private wireEngineEvents(): void {
    this.engine.on('image-loaded', () => {
      // Hide upload area, show canvas + controls
      const uploadArea = document.getElementById('advUploadArea');
      const editorSection = document.getElementById('advEditorSection');
      const generateBtn = document.getElementById('advGenerateBtn') as HTMLButtonElement;

      if (uploadArea) uploadArea.style.display = 'none';
      if (editorSection) editorSection.style.display = '';
      if (generateBtn) generateBtn.disabled = false;

      // Refresh canvas display size now that container is visible
      requestAnimationFrame(() => this.cm.refreshDisplay());
      this.updateDimensions();
    });

    this.engine.on('image-updated', () => {
      this.updateDimensions();
      this.resetSliders();
    });

    this.engine.on('history-changed', () => {
      const undoBtn = document.getElementById('advUndoBtn') as HTMLButtonElement;
      const redoBtn = document.getElementById('advRedoBtn') as HTMLButtonElement;
      if (undoBtn) undoBtn.disabled = !this.engine.canUndo;
      if (redoBtn) redoBtn.disabled = !this.engine.canRedo;
    });

    this.engine.on('crop-mode-changed', () => {
      if (this.engine.cropMode) {
        this.cropOverlay.activate();
      } else {
        this.cropOverlay.deactivate();
      }
    });
  }

  private wireSliders(): void {
    const brightness = document.getElementById('advBrightness') as HTMLInputElement;
    const contrast = document.getElementById('advContrast') as HTMLInputElement;
    const blur = document.getElementById('advBlur') as HTMLInputElement;
    const sharpen = document.getElementById('advSharpen') as HTMLInputElement;
    const rotation = document.getElementById('advRotation') as HTMLInputElement;

    const brightnessVal = document.getElementById('advBrightnessValue');
    const contrastVal = document.getElementById('advContrastValue');
    const blurVal = document.getElementById('advBlurValue');
    const sharpenVal = document.getElementById('advSharpenValue');
    const rotationVal = document.getElementById('advRotationValue');

    brightness?.addEventListener('input', () => {
      if (brightnessVal) brightnessVal.textContent = brightness.value;
      this.engine.setAdjustment('brightness', Number(brightness.value));
    });

    contrast?.addEventListener('input', () => {
      if (contrastVal) contrastVal.textContent = contrast.value;
      this.engine.setAdjustment('contrast', Number(contrast.value));
    });

    blur?.addEventListener('input', () => {
      if (blurVal) blurVal.textContent = blur.value;
      this.engine.setAdjustment('blur', Number(blur.value));
    });

    sharpen?.addEventListener('input', () => {
      if (sharpenVal) sharpenVal.textContent = sharpen.value;
      this.engine.setAdjustment('sharpen', Number(sharpen.value));
    });

    rotation?.addEventListener('input', () => {
      if (rotationVal) rotationVal.textContent = rotation.value;
      this.engine.setAdjustment('rotation', Number(rotation.value));
    });
  }

  private wireCheckboxes(): void {
    const grayscale = document.getElementById('advGrayscale') as HTMLInputElement;

    grayscale?.addEventListener('change', () => {
      this.engine.setAdjustment('grayscale', grayscale.checked);
    });
  }

  private wireToolbar(): void {
    document.getElementById('advUndoBtn')?.addEventListener('click', () => this.engine.undo());
    document.getElementById('advRedoBtn')?.addEventListener('click', () => this.engine.redo());
    document.getElementById('advResetAllBtn')?.addEventListener('click', () => {
      this.engine.resetAll();
      this.resetSliders();
    });
  }

  private wireTransformButtons(): void {
    document.getElementById('advRotateCCW')?.addEventListener('click', () => this.engine.rotate90(-1));
    document.getElementById('advRotate180')?.addEventListener('click', () => this.engine.rotate180());
    document.getElementById('advRotateCW')?.addEventListener('click', () => this.engine.rotate90(1));
    document.getElementById('advFlipH')?.addEventListener('click', () => this.engine.flipH());
    document.getElementById('advFlipV')?.addEventListener('click', () => this.engine.flipV());
    document.getElementById('advCropBtn')?.addEventListener('click', () => this.engine.toggleCropMode());
    document.getElementById('advResizeBtn')?.addEventListener('click', () => this.showResizeControls());
  }

  private wireResizeControls(): void {
    const widthSlider = document.getElementById('advResizeWidth') as HTMLInputElement;
    const heightSlider = document.getElementById('advResizeHeight') as HTMLInputElement;
    const widthVal = document.getElementById('advResizeWidthValue');
    const heightVal = document.getElementById('advResizeHeightValue');
    const lockCheckbox = document.getElementById('advAspectLock') as HTMLInputElement;

    widthSlider?.addEventListener('input', () => {
      const w = Number(widthSlider.value);
      if (widthVal) widthVal.textContent = String(w);
      if (this.resizeAspectLocked && heightSlider) {
        const h = Math.max(1, Math.round(w / this.resizeAspectRatio));
        heightSlider.value = String(h);
        if (heightVal) heightVal.textContent = String(h);
      }
    });

    heightSlider?.addEventListener('input', () => {
      const h = Number(heightSlider.value);
      if (heightVal) heightVal.textContent = String(h);
      if (this.resizeAspectLocked && widthSlider) {
        const w = Math.max(1, Math.round(h * this.resizeAspectRatio));
        widthSlider.value = String(w);
        if (widthVal) widthVal.textContent = String(w);
      }
    });

    lockCheckbox?.addEventListener('change', () => {
      this.resizeAspectLocked = lockCheckbox.checked;
    });

    document.getElementById('advResizeApplyBtn')?.addEventListener('click', () => {
      const w = Number(widthSlider?.value ?? 0);
      const h = Number(heightSlider?.value ?? 0);
      if (w > 0 && h > 0) {
        this.engine.applyResize(w, h);
        this.hideResizeControls();
      }
    });

    document.getElementById('advResizeCancelBtn')?.addEventListener('click', () => {
      this.hideResizeControls();
    });
  }

  private showResizeControls(): void {
    const controls = document.getElementById('advResizeControls');
    const widthSlider = document.getElementById('advResizeWidth') as HTMLInputElement;
    const heightSlider = document.getElementById('advResizeHeight') as HTMLInputElement;
    const widthVal = document.getElementById('advResizeWidthValue');
    const heightVal = document.getElementById('advResizeHeightValue');

    const w = this.engine.imageWidth;
    const h = this.engine.imageHeight;
    this.resizeAspectRatio = w / h;

    if (widthSlider) { widthSlider.value = String(w); widthSlider.max = String(w * 4); }
    if (heightSlider) { heightSlider.value = String(h); heightSlider.max = String(h * 4); }
    if (widthVal) widthVal.textContent = String(w);
    if (heightVal) heightVal.textContent = String(h);

    controls?.classList.remove('hidden');
  }

  private hideResizeControls(): void {
    document.getElementById('advResizeControls')?.classList.add('hidden');
  }

  private wireKeyboardShortcuts(): void {
    document.addEventListener('keydown', (e) => {
      if (!this.engine.hasImage) return;

      // Ctrl+Z / Cmd+Z = undo, Ctrl+Shift+Z / Cmd+Shift+Z = redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          this.engine.redo();
        } else {
          this.engine.undo();
        }
      }
      // Ctrl+Y / Cmd+Y = redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        this.engine.redo();
      }
    });
  }

  private wireDownloadButton(): void {
    document.getElementById('advGenerateBtn')?.addEventListener('click', async () => {
      if (!this.engine.hasImage) return;

      const btn = document.getElementById('advGenerateBtn') as HTMLButtonElement;
      const btnText = document.getElementById('advGenerateBtnText');
      const originalText = btnText?.textContent;

      if (btn) btn.disabled = true;
      if (btnText) btnText.textContent = 'Processing...';

      try {
        const blob = await this.engine.getExportBlob(this.selectedFormat);

        this.modal.showCountdown(() => {
          const ext = this.selectedFormat === 'jpeg' ? 'jpg' : this.selectedFormat;
          downloadBlob(blob, `edited-image.${ext}`);
          this.modal.close();
        });
      } catch (error) {
        console.error('Error exporting image:', error);
        alert('Error exporting image. Please try again.');
      } finally {
        if (btn) btn.disabled = false;
        if (btnText) btnText.textContent = originalText || 'Download';
      }
    });
  }

  private wireClearButton(): void {
    document.getElementById('advClearImageBtn')?.addEventListener('click', () => {
      // Show upload area, hide canvas + format sections
      const uploadArea = document.getElementById('advUploadArea');
      const editorSection = document.getElementById('advEditorSection');
      const generateBtn = document.getElementById('advGenerateBtn') as HTMLButtonElement;

      if (uploadArea) uploadArea.style.display = '';
      if (editorSection) editorSection.style.display = 'none';
      if (generateBtn) generateBtn.disabled = true;

      this.resetSliders();
      this.hideResizeControls();
    });
  }

  private updateDimensions(): void {
    const dims = document.getElementById('advDimensions');
    if (dims) {
      dims.textContent = `${this.engine.imageWidth} × ${this.engine.imageHeight} px`;
    }
  }

  private resetSliders(): void {
    const setSlider = (id: string, value: number, valueId: string) => {
      const slider = document.getElementById(id) as HTMLInputElement;
      const display = document.getElementById(valueId);
      if (slider) slider.value = String(value);
      if (display) display.textContent = String(value);
    };

    setSlider('advBrightness', 0, 'advBrightnessValue');
    setSlider('advContrast', 0, 'advContrastValue');
    setSlider('advBlur', 0, 'advBlurValue');
    setSlider('advSharpen', 0, 'advSharpenValue');
    setSlider('advRotation', 0, 'advRotationValue');

    const grayscale = document.getElementById('advGrayscale') as HTMLInputElement;
    if (grayscale) grayscale.checked = false;
  }

  getToolName(): string {
    return 'Advanced Image Editor';
  }

  destroy(): void {
    // Cleanup if needed
  }
}

// Module initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const tool = new AdvancedImageEditorTool();
    tool.init();
  });
} else {
  const tool = new AdvancedImageEditorTool();
  tool.init();
}
